import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Label } from '../../components/ui/label.jsx';
import { Button } from '../../components/ui/button.jsx';
import { useToast } from '../../hooks/use-toast.js';
import axios from '../../lib/axios.js'
import { SelectTimeZone } from '../../components/SelectTimeZone/SelectTimeZone.jsx';
import { SelectCommunityStatus } from '../../components/SelectCommunityStatus/SelectCommunityStatus.jsx';
import { getUserCompanyId, useIsUserAdmin } from '../../hooks/authHelpers.js';
import SearchSelectCompanies from '@/components/SearchSelectCompanies/SearchSelectCompanies.jsx';
import { PhoneNumberInput } from '@/components/ui/phone-number-input.jsx';

const CreateCommunity = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const isAdmin = useIsUserAdmin();
    const [community, setCommunity] = useState({
        community_name: '',
        community_phone_number: '',
        secondary_community_phone_number: '',
        assigned_flair_phone_number: '',
        assigned_flair_email: '',
        community_phone_number_ext: '',
        time_zone: 'US/Eastern',
        companies: !isAdmin ? [{ id: getUserCompanyId() }] : [],
        status: isAdmin ? 'active' : 'secret-shopper',
        website_url: '',
    });

    const isSecretShopper = community.status === 'secret-shopper';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCommunity(prev => ({ ...prev, [name]: value }));
    };

    const handleTimeZoneChange = (timezone) => {
        setCommunity(prev => ({ ...prev, time_zone: timezone }))
    }

    const handleStatusChange = (status) => {
        setCommunity(prev => ({ ...prev, status }))
    }

    const handleCompanyChange = (companies) => {
        setCommunity(prev => ({ ...prev, companies }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = { ...community }

        if (!data.website_url) {
            data.website_url = null;
        }

        try {
            const response = await axios.post('/communities', {
                headers: {
                    'Content-Type': 'application/json'
                },
                ...data,
                companies: community.companies?.map(({ id }) => id)
            });
            const { community: newCommunity } = response.data;
            toast({
                title: "Success",
                description: "Community created successfully.",
            });
            navigate(`/communities/${newCommunity.id}`);
        } catch (error) {
            console.error('Error creating community:', error);
            toast({
                title: "Error creating community",
                description: `Failed to create community. ${error.message}`,
                variant: "destructive",
            });
        }
    };

    return (
        <div className="min-h-full min-w-full flex flex-col">
            <Card className="w-full max-w-7xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-6 p-6">
                    <div className="space-y-2">
                        <h1 className="text-3xl font-bold text-center">Create New Community</h1>
                    </div>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="community_name">Community Name</Label>
                            <Input
                                id="community_name"
                                name="community_name"
                                value={community.community_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="community_phone_number">Community Phone Number</Label>
                            <PhoneNumberInput
                                id="community_phone_number"
                                name="community_phone_number"
                                value={community.community_phone_number}
                                onChange={handleChange}
                                required
                                pattern="^(\D*\d\D*){10}$"
                                title="Please enter a valid 10-digit phone number."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="secondary_community_phone_number">Secondary Community Phone Number</Label>
                            <PhoneNumberInput
                                id="secondary_community_phone_number"
                                name="secondary_community_phone_number"
                                value={community.secondary_community_phone_number}
                                onChange={handleChange}
                                pattern="^(\D*\d\D*){10}$"
                                title="Please enter a valid 10-digit phone number."
                            />
                        </div>
                        {
                            isAdmin && <div className="space-y-2">
                                <Label htmlFor="assigned_flair_phone_number">Assigned Flair Phone Number</Label>
                                <PhoneNumberInput
                                    id="assigned_flair_phone_number"
                                    name="assigned_flair_phone_number"
                                    value={community.assigned_flair_phone_number}
                                    onChange={handleChange}
                                    required={!isSecretShopper}
                                    pattern="^(\D*\d\D*){10}$"
                                    title="Please enter a valid 10-digit phone number."
                                />
                            </div>
                        }
                        {
                            isAdmin && <div className="space-y-2">
                                <Label htmlFor="assigned_flair_email">Assigned Flair Email</Label>
                                <Input
                                    id="assigned_flair_email"
                                    name="assigned_flair_email"
                                    type="email"
                                    value={community.assigned_flair_email}
                                    onChange={handleChange}
                                    required={!isSecretShopper}
                                />
                            </div>
                        }
                        <div className="space-y-2">
                            <Label htmlFor="community_phone_number_ext">Community Phone Number Extension</Label>
                            <Input
                                id="community_phone_number_ext"
                                name="community_phone_number_ext"
                                value={community.community_phone_number_ext}
                                onChange={handleChange}
                            />
                        </div>
                        {isAdmin && <div className="space-y-2">
                            <Label htmlFor="monthly_price">Monthly Subscription Price</Label>
                            <Input
                                id="monthly_price"
                                name="monthly_price"
                                value={community.monthly_price}
                                onChange={handleChange}
                            />
                        </div>
                        }
                        {isAdmin && (
                            <div className="space-y-2">
                                <Label>Select Companies</Label>
                                <SearchSelectCompanies onChange={handleCompanyChange} />
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="time_zone">Time Zone</Label>
                            <SelectTimeZone
                                id="time_zone"
                                name="time_zone"
                                required
                                value={community.time_zone || 'US/Eastern'}
                                onValueChange={handleTimeZoneChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="website_url">Website URL</Label>
                            <Input
                                id="website_url"
                                name="website_url"
                                type="url"
                                placeholder="https://example.com"
                                value={community.website_url}
                                onChange={handleChange}
                                required={isSecretShopper}
                            />
                        </div>
                        {
                            isAdmin && <div className="space-y-2">
                                <Label htmlFor="status">Status</Label>
                                <SelectCommunityStatus
                                    id="status"
                                    name="status"
                                    required
                                    value={community.status || ''}
                                    onValueChange={handleStatusChange}
                                />
                            </div>
                        }
                    </div>
                    <Button type="submit" className="w-full">
                        Create Community
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default CreateCommunity;